import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Spinner, Table, Badge, Form, InputGroup, Modal, Button as BootstrapButton } from 'react-bootstrap';
import ShopifyHeader from '../ShopifyHeader';
import ProductsSubMenu from './ProductsSubMenu';

const ShopifyProducts = () => {
    const { shop: shopParam } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [meliFilter, setMeliFilter] = useState('meli');
    const [showSyncDialog, setShowSyncDialog] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState('');
    const [syncSuccess, setSyncSuccess] = useState(false);

    const shop = shopParam ? `${shopParam}.myshopify.com` : null;

    useEffect(() => {
        if (!shop) {
            setError('Missing shop parameter. Please reinstall the app.');
            setIsLoading(false);
            return;
        }
        loadProducts();
    }, [shop]); // eslint-disable-line react-hooks/exhaustive-deps

    // Helper function to transform products
    const transformProducts = (products) => {
        return products.map(product => {
            const firstImage = product.images && product.images.length > 0 ? product.images[0] : null;
            const firstVariant = product.variants && product.variants.length > 0 ? product.variants[0] : null;

            return {
                id: product.id,
                publishedOnChannel: product.publishedOnChannel,
                title: product.title,
                description: product.description,
                weight: product.weight || '',
                weight_unit: product.weight_unit || '',
                handle: product.handle || '',
                status: product.status?.toLowerCase() || 'draft',
                vendor: product.vendor || '',
                productType: product.productType || '',
                createdAt: product.createdAt || '',
                updatedAt: product.updatedAt || '',
                publishedAt: product.publishedAt || '',
                tags: product.tags || [],
                image_url: firstImage?.url || '',
                image_alt: firstImage?.altText || '',
                sku: firstVariant?.sku || '',
                barcode: firstVariant?.barcode || '',
                price: firstVariant?.price || '0.00',
                inventory: firstVariant?.inventoryQuantity || 0,
                totalInventory: product.totalInventory || 0,
                variants_count: product.variants?.length || 0,
                images_count: product.images?.length || 0,
                synced_to_meli: false,
                meli_status: null,
                variants: product.variants?.map(variant => ({
                    id: variant.id,
                    title: variant.title || 'Default Title',
                    price: variant.price,
                    compareAtPrice: variant.compareAtPrice,
                    sku: variant.sku,
                    barcode: variant.barcode,
                    inventoryQuantity: variant.inventoryQuantity,
                    availableForSale: variant.availableForSale,
                    selectedOptions: variant.selectedOptions || []
                })) || [],
                images: product.images?.map(image => ({
                    id: image.id,
                    url: image.url,
                    altText: image.altText,
                    width: image.width,
                    height: image.height
                })) || []
            };
        });
    };

    // Helper function to get variant display name
    const getVariantDisplayName = (selectedOptions) => {
        if (!selectedOptions || selectedOptions.length === 0) return 'Default';

        return selectedOptions
            .map(option => option.value)
            .join(', ')
            .replace('Default', '')
            .replace('Title', 'Default')
            .trim() || 'Default';
    };

    // Helper function to calculate price range
    const getPriceRange = (variants) => {
        if (!variants || variants.length === 0) return { min: 0, max: 0 };

        const prices = variants.map(v => parseFloat(v.price) || 0);
        return {
            min: Math.min(...prices),
            max: Math.max(...prices)
        };
    };

    // Helper function to render SKU/UPC badge
    const renderIdentifierBadge = (value, label, variant = 'danger') => {
        if (!value) {
            return <div><Badge bg={variant}>No {label}</Badge></div>;
        }
        return <>{label} <b>{value}</b></>;
    };

    // Helper function to render variant identifier
    const renderVariantIdentifier = (value, label) => {
        if (!value) {
            return <div><Badge bg="danger">No {label}</Badge></div>;
        }
        return <small className="text-muted d-block">{label}: {value}</small>;
    };

    // Helper function to render product identifiers
    const renderProductIdentifiers = (product) => (
        <>
            <div className='mb-1'>
                {renderIdentifierBadge(product.sku, 'SKU')}
            </div>
            <div>
                {renderIdentifierBadge(product.barcode, 'UPC')}
            </div>
            <div>
                {product.weight ? (
                    <small className="text-muted">
                        Weight: {product.weight} {product.weight_unit || 'lbs'}
                    </small>
                ) : (
                    <Badge className=" bg-danger">
                        No WEIGHT
                    </Badge>
                )}
            </div>
        </>
    );

    // Helper function to render variant item
    const renderVariantItem = (variant, index) => {
        const displayName = getVariantDisplayName(variant.selectedOptions);

        return (
            <div key={variant.id || index} className="variant-item mb-2 p-2 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                        <div className="fw-bold text-primary">{displayName}</div>
                        <small className="text-muted d-block">
                            ID: {variant.id?.split('/').pop() || 'N/A'}
                        </small>
                        {renderVariantIdentifier(variant.sku, 'SKU')}
                        {renderVariantIdentifier(variant.barcode, 'UPC')}
                    </div>
                    <div className="text-end">
                        <div className="fw-bold text-success">
                            ${variant.price || '0.00'}
                        </div>
                        <div className={`fw-bold ${variant.inventoryQuantity === 0 ? 'text-danger' : 'text-success'}`}>
                            {variant.inventoryQuantity || 0} units
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Helper function to render variants list
    const renderVariantsList = (product) => (
        <div className="mt-3">
            <h6 className="mb-2">Variants:</h6>
            <div className="variants-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {product.variants && product.variants.length > 0 ? (
                    product.variants.map((variant, index) => renderVariantItem(variant, index))
                ) : (
                    <div className="text-muted">No variants available</div>
                )}
            </div>
        </div>
    );

    // Helper function to render product summary
    const renderProductSummary = (product) => {
        const priceRange = getPriceRange(product.variants);
        const isSinglePrice = product.variants.length === 1;

        return (
            <div className="mt-2 pt-2 border-top w-100">
                <div className="d-flex justify-content-between">
                    <span className="fw-bold">Inventory</span>
                    <span className={`fw-bold ${product.totalInventory === 0 ? 'text-danger' : 'text-success'}`}>
                        {product.totalInventory || 0} units
                    </span>
                </div>
                <div className="d-flex justify-content-between">
                    <span className="fw-bold">Price</span>
                    <span className="text-primary">
                        {isSinglePrice ? (
                            `$${priceRange.min.toFixed(2)}`
                        ) : (
                            `$${priceRange.min.toFixed(2)} - $${priceRange.max.toFixed(2)}`
                        )}
                    </span>
                </div>
            </div>
        );
    };

    // Helper function to render MELI status badge
    const getMeliStatusBadge = (syncedToMeli, meliStatus) => {
        if (!syncedToMeli) {
            return <Badge bg="secondary">Not Synced</Badge>;
        }

        const statusMap = {
            'published': { bg: 'success', text: 'Published' },
            'paused': { bg: 'warning', text: 'Paused' },
            'ended': { bg: 'danger', text: 'Ended' }
        };

        const status = statusMap[meliStatus] || { bg: 'info', text: 'Synced' };
        return <Badge bg={status.bg}>{status.text}</Badge>;
    };

    // Helper function to filter products
    const filterProducts = (products) => {
        return products.filter(product => {
            const matchesSearch = (product.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (product.category || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (product.sku || '').toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = filterStatus === 'all' ||
                (filterStatus === 'synced' && product.synced_to_meli) ||
                (filterStatus === 'not_synced' && !product.synced_to_meli) ||
                (filterStatus === 'active' && product.status === 'active') ||
                (filterStatus === 'draft' && product.status === 'draft');

            const matchesMeliFilter = meliFilter === 'all' ||
                (meliFilter === 'meli' && product.publishedOnChannel === true) ||
                (meliFilter === 'not_meli' && product.publishedOnChannel !== true);

            return matchesSearch && matchesStatus && matchesMeliFilter;
        });
    };

    const loadProducts = async () => {
        try {
            setIsLoading(true);
            setError('');

            const { ShopifyAuth } = await import('../../utils/shopifyAuth');
            let authData = await ShopifyAuth.getAuthDataWithRenewal();

            console.log('🔍 Products - Auth data after renewal attempt:', authData);

            if (!authData.isAuthenticated) {
                console.error('❌ Authentication failed even after renewal attempt');
                setError('Authentication expired. Please reinstall the app.');
                setIsLoading(false);
                return;
            }

            const shopDomain = shopParam || sessionStorage.getItem('shopify_shop');

            const response = await fetch('https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/shopify/products?shopify_shop=' + shopDomain, {
                method: 'GET',
                headers: {

                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

                if (response.status === 401) {
                    if (errorData.error_code === 'TOKEN_EXPIRED' && errorData.renewal_required) {
                        try {
                            console.log('🔄 Token expired, attempting renewal...');
                            await ShopifyAuth.renewAuthToken();

                            const newAuthData = ShopifyAuth.getAuthData(); // eslint-disable-line no-unused-vars
                            const shopDomain = shopParam || sessionStorage.getItem('shopify_shop');
                            const retryResponse = await fetch('https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/shopify/products?shopify_shop=' + shopDomain, {
                                method: 'GET',
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            });

                            if (retryResponse.ok) {
                                const retryResult = await retryResponse.json();
                                console.log('🔄 Retry response after token renewal:', retryResult);

                                if (retryResult.success) {
                                    const transformedProducts = transformProducts(retryResult.products);
                                    setProducts(transformedProducts);
                                    setIsLoading(false);
                                    return;
                                } else {
                                    throw new Error(retryResult.error || 'Failed to load products after token renewal');
                                }
                            } else {
                                throw new Error(`Retry failed with status: ${retryResponse.status}`);
                            }
                        } catch (renewError) {
                            console.error('Failed to renew token:', renewError);
                        }
                    }

                    ShopifyAuth.clearAuthData();
                    setError('Authentication expired. Please reinstall the app.');
                    setIsLoading(false);
                    return;
                }

                throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();

            if (result.success) {
                const transformedProducts = transformProducts(result.products);
                setProducts(transformedProducts);
            } else {
                setError(result.error || 'Failed to load products');
            }

        } catch (error) {
            console.error('Error loading products:', error);
            setError(error.message || 'Failed to load products');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSyncAll = () => {
        setShowSyncDialog(true);
    };

    const handleConfirmSync = async () => {
        try {
            setIsSyncing(true);
            setSyncMessage('Starting sync...');
            setSyncSuccess(false);

            // Get shop domain from URL or sessionStorage
            const shopDomain = shopParam || sessionStorage.getItem('shopify_shop');

            if (!shopDomain) {
                setError('No shop domain found. Please refresh the page.');
                setSyncMessage('Error: No shop domain found');
                return;
            }

            // Hide product list during sync
            setProducts([]);
            setIsLoading(true);

            // Call the sync API endpoint
            const response = await fetch('https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/shopify/sync', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    shop_domain: shopDomain,
                    action: 'sync_products'
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();

            if (result.success) {
                setSyncMessage(`Sync completed successfully! ${result.message}`);
                setSyncSuccess(true);

                // Refresh the products list
                await loadProducts();
            } else {
                throw new Error(result.error || 'Sync failed');
            }

        } catch (error) {
            console.error('Sync error:', error);
            setSyncMessage(`Sync failed: ${error.message}`);
            setSyncSuccess(false);
            setError(`Sync failed: ${error.message}`);
        } finally {
            setIsSyncing(false);
        }
    };

    const handleCancelSync = () => {
        setShowSyncDialog(false);
        setSyncMessage('');
        setSyncSuccess(false);
    };

    const filteredProducts = filterProducts(products);

    if (error) {
        return (
            <div style={{ minHeight: '100vh' }}>
                <ShopifyHeader />
                <Container className="py-5">
                    <Row className="justify-content-center">
                        <Col md={6}>
                            <Alert variant="danger">
                                <Alert.Heading>Error Loading Products</Alert.Heading>
                                <p>{error}</p>
                                <Button variant="outline-danger" onClick={loadProducts}>
                                    Try Again
                                </Button>
                            </Alert>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh' }}>
            <ShopifyHeader />

            <Container className="py-4">
                {/* Products Sub Menu */}
                <ProductsSubMenu
                    activeTab="shopify"
                    onTabChange={(tab) => {
                        if (tab === 'meli') {
                            window.location.href = `/shopify/${shopParam}/meli-products`;
                        }
                    }}
                />

                {/* Page Header */}
                <Row className="mb-4">
                    <Col>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h2>Shopify Products</h2>
                            </div>
                            <Button
                                variant="success"
                                onClick={handleSyncAll}
                                disabled={isLoading || isSyncing}
                                className="me-2"
                            >
                                {isSyncing ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Syncing...
                                    </>
                                ) : (
                                    'Sync All Products'
                                )}
                            </Button>
                        </div>
                    </Col>
                </Row>

                {/* Filters and Search */}
                <Row className="mb-4">
                    <Col md={3}>
                        <Form.Select
                            value={meliFilter}
                            onChange={(e) => setMeliFilter(e.target.value)}
                        >
                            <option value="meli">MELI</option>
                            <option value="not_meli">Not in MELI</option>
                            <option value="all">All Products</option>
                        </Form.Select>
                    </Col>
                    <Col md={6}>
                        <InputGroup>
                            <InputGroup.Text>
                                <i className="fas fa-search"></i>
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Search products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    <Col md={3}>
                        <Form.Select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="all">All Products</option>
                            <option value="active">Active Only</option>
                            <option value="draft">Draft Only</option>
                            <option value="synced">Synced to MELI</option>
                            <option value="not_synced">Not Synced</option>
                        </Form.Select>
                    </Col>
                    <Col md={3}>
                        <div className="text-end">
                            <small className="text-muted">
                                {filteredProducts.length} of {products.length} products
                            </small>
                        </div>
                    </Col>
                </Row>

                {/* Products Table */}
                <Card>
                    <Card.Body className="p-0">
                        {isLoading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="success" />
                                <p className="mt-3 text-muted">Loading products...</p>
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
                                <h5>No products found</h5>
                                <p className="text-muted">
                                    {searchTerm || filterStatus !== 'all'
                                        ? 'Try adjusting your search or filters'
                                        : 'No products available in your store'
                                    }
                                </p>
                            </div>
                        ) : (
                            <Table responsive hover className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: '60%' }}>Product</th>
                                        <th style={{ width: '40%' }}>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredProducts.map((product) => (
                                        <tr key={product.id}>
                                            {/* Column 1: Image, Title, and Variants */}
                                            <td>
                                                <div className="d-flex align-items-start">
                                                    {product.image_url && (
                                                        <img
                                                            src={product.image_url}
                                                            alt={product.image_alt}
                                                            className="me-3"
                                                            style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
                                                        />
                                                    )}
                                                    <div className="flex-grow-1">
                                                        <h6 className="mb-1">{String(product.title || 'Untitled Product')}</h6>
                                                        {product.description ? (
                                                            <p className="text-muted mb-3">
                                                                {product.description}
                                                            </p>
                                                        ) : (
                                                            <Badge className="text-white mb-3 bg-danger">
                                                                No description
                                                            </Badge>
                                                        )}
                                                        {renderProductIdentifiers(product)}
                                                        <small className="text-muted">
                                                            {String(product.variants_count || 0)} variant(s)
                                                        </small>
                                                        {renderVariantsList(product)}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Column 2: Status and Summary */}
                                            <td>
                                                <div className="d-flex flex-column align-items-start">
                                                    {/* MELI Status */}
                                                    <div className="mb-2">
                                                        {product.publishedOnChannel === true && (
                                                            <Badge bg="info">
                                                                <img width={145} src="/images/logo_large_plus@2x.webp" alt="Published" className="me-2" />
                                                            </Badge>
                                                        )}
                                                    </div>

                                                    {/* Product Summary */}
                                                    {renderProductSummary(product)}

                                                    {/* MELI Status Badge */}
                                                    <div className="mt-2">
                                                        {getMeliStatusBadge(product.synced_to_meli, product.meli_status)}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>
            </Container>

            {/* Sync Confirmation Dialog */}
            <Modal show={showSyncDialog} onHide={handleCancelSync} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Sync All Products</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!isSyncing && !syncMessage ? (
                        <>
                            <p>Are you sure you want to sync all products from Shopify to the database?</p>
                            <p className="text-muted">
                                This will fetch all products published on the A360 channel and update the database.
                            </p>
                            <div className="alert alert-info">
                                <strong>Note:</strong> This operation may take a few minutes depending on the number of products.
                            </div>
                        </>
                    ) : (
                        <div className="text-center">
                            {isSyncing ? (
                                <>
                                    <Spinner animation="border" variant="success" className="mb-3" />
                                    <p className="mb-0">{syncMessage}</p>
                                </>
                            ) : (
                                <>
                                    <div className={`alert ${syncSuccess ? 'alert-success' : 'alert-danger'} mb-3`}>
                                        <i className={`fas ${syncSuccess ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2`}></i>
                                        {syncMessage}
                                    </div>
                                    {syncSuccess && (
                                        <p className="text-muted">
                                            Products have been refreshed and are now up to date.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <BootstrapButton
                        variant="secondary"
                        onClick={handleCancelSync}
                        disabled={isSyncing}
                    >
                        {syncSuccess ? 'Close' : 'Cancel'}
                    </BootstrapButton>
                    {!syncSuccess && (
                        <BootstrapButton
                            variant="success"
                            onClick={handleConfirmSync}
                            disabled={isSyncing}
                        >
                            {isSyncing ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Syncing...
                                </>
                            ) : (
                                'Sync Now'
                            )}
                        </BootstrapButton>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ShopifyProducts;
